'use client';

import { useState, useMemo, useCallback } from 'react';
import { elements, Element } from '@/data/elements';
import { ElementCard } from '@/components/ElementCard';
import { ElementDialog } from '@/components/ElementDialog';
import { PeriodicTable } from '@/components/PeriodicTable';
import { GoogleAdBanner } from '@/components/GoogleAd';
import { Button } from '@/components/ui/button';
import { Filter, Grid3x3, Table2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

export default function Home() {
  const t = useTranslations('Home');
  const [selectedElement, setSelectedElement] = useState<Element | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'periodic'>('periodic');

  const handleElementClick = useCallback((element: Element) => {
    setSelectedElement(element);
    setDialogOpen(true);
  }, []);

  const filteredElements = useMemo(() =>
    elements.filter((element) => {
      const stateMatch = stateFilter === 'all' || element.state === stateFilter;
      const categoryMatch = categoryFilter === 'all' || element.category === categoryFilter;
      return stateMatch && categoryMatch;
    }),
    [stateFilter, categoryFilter]
  );

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <h1 className="text-3xl sm:text-5xl font-bold dark:text-white light:text-gray-900 mb-4">
          {t('title')} <span className="dark:text-[#64ffda] light:text-teal-600">{t('titleHighlight')}</span>
        </h1>
        <p className="text-base sm:text-xl dark:text-[#a8b2d1] light:text-gray-600 max-w-2xl mx-auto">
          {t('subtitle')}
        </p>
        <div className="mt-6 flex items-center justify-center gap-4 text-sm dark:text-[#64ffda] light:text-teal-600">
          <span className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full dark:bg-[#64ffda] light:bg-teal-600"></div>
            {t('elementsDisplayed', { count: filteredElements.length })}
          </span>
        </div>
      </div>

      <GoogleAdBanner />

      {/* View Mode Toggle */}
      <div className="mb-8 flex justify-center gap-3">
        <Button
          variant={viewMode === 'periodic' ? 'default' : 'outline'}
          onClick={() => setViewMode('periodic')}
          className={
            viewMode === 'periodic'
              ? 'bg-[#64ffda] text-[#0a192f] hover:bg-[#64ffda]/90'
              : 'border-[#233554] text-[#a8b2d1] hover:text-[#64ffda] hover:border-[#64ffda]'
          }
        >
          <Table2 className="w-4 h-4 mr-2" />
          {t('periodicTableView')}
        </Button>
        <Button
          variant={viewMode === 'grid' ? 'default' : 'outline'}
          onClick={() => setViewMode('grid')}
          className={
            viewMode === 'grid'
              ? 'bg-[#64ffda] text-[#0a192f] hover:bg-[#64ffda]/90'
              : 'border-[#233554] text-[#a8b2d1] hover:text-[#64ffda] hover:border-[#64ffda]'
          }
        >
          <Grid3x3 className="w-4 h-4 mr-2" />
          {t('gridView')}
        </Button>
      </div>

      {/* Filters */}
      {viewMode === 'grid' && (
        <div className="mb-8 p-6 dark:bg-[#112240] light:bg-gray-50 rounded-xl dark:border-[#233554] light:border-gray-200 border">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-5 h-5 dark:text-[#64ffda] light:text-teal-600" />
            <h2 className="text-lg font-semibold dark:text-white light:text-gray-900">{t('filters')}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* State Filter */}
            <div>
              <label className="text-sm dark:text-[#a8b2d1] light:text-gray-700 mb-2 block">{t('stateOfMatter')}</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: t('all') },
                  { value: 'solid', label: t('solid') },
                  { value: 'liquid', label: t('liquid') },
                  { value: 'gas', label: t('gas') },
                ].map((filter) => (
                  <Button
                    key={filter.value}
                    variant={stateFilter === filter.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStateFilter(filter.value)}
                    className={
                      stateFilter === filter.value
                        ? 'bg-[#64ffda] text-[#0a192f] hover:bg-[#64ffda]/90'
                        : 'border-[#233554] text-[#a8b2d1] hover:text-[#64ffda] hover:border-[#64ffda]'
                    }
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="text-sm dark:text-[#a8b2d1] light:text-gray-700 mb-2 block">{t('category')}</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: t('all') },
                  { value: 'nonmetal', label: t('nonmetal') },
                  { value: 'noble_gas', label: t('nobleGas') },
                  { value: 'alkali_metal', label: t('alkaliMetal') },
                  { value: 'transition_metal', label: t('transitionMetal') },
                ].map((filter) => (
                  <Button
                    key={filter.value}
                    variant={categoryFilter === filter.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCategoryFilter(filter.value)}
                    className={
                      categoryFilter === filter.value
                        ? 'bg-[#64ffda] text-[#0a192f] hover:bg-[#64ffda]/90'
                        : 'border-[#233554] text-[#a8b2d1] hover:text-[#64ffda] hover:border-[#64ffda]'
                    }
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'periodic' && (
        <PeriodicTable elements={elements} onElementClick={handleElementClick} />
      )}

      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {filteredElements.map((element) => (
            <ElementCard
              key={element.id}
              element={element}
              onClick={() => handleElementClick(element)}
            />
          ))}
        </div>
      )}

      <GoogleAdBanner />

      <ElementDialog
        element={selectedElement}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
